import React, { useState, useEffect } from 'react';
import { TimelineItem, EditableTimelineItem } from '../types';
import { apiService } from '../services/api';

interface TimelineEditorProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  initialTimeline: TimelineItem[];
  onSave: (updatedTimeline: TimelineItem[]) => void;
}

const TimelineEditor: React.FC<TimelineEditorProps> = ({
  isOpen,
  onClose,
  clientId,
  initialTimeline,
  onSave
}) => {
  const [timelineItems, setTimelineItems] = useState<EditableTimelineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Convert TimelineItem[] to EditableTimelineItem[]
      const editableItems: EditableTimelineItem[] = initialTimeline.map((item, index) => ({
        ...item,
        order_index: index
      }));
      
      setTimelineItems(editableItems);
      setError(null);
    }
  }, [isOpen, initialTimeline]);

  const handleItemChange = (id: string, field: keyof TimelineItem, value: string) => {
    setTimelineItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addTimelineItem = () => {
    const newItem: EditableTimelineItem = {
      id: `temp_${Date.now()}`,
      date: '',
      title: '',
      description: '',
      emoji: '',
      order_index: timelineItems.filter(item => !item.isDeleted).length,
      isNew: true
    };
    setTimelineItems(prev => [...prev, newItem]);
  };

  const removeTimelineItem = (id: string) => {
    setTimelineItems(prev => prev.map(item => 
      item.id === id ? { ...item, isDeleted: true } : item
    ));
  };

  const moveItemUp = (id: string) => {
    const activeItems = timelineItems.filter(item => !item.isDeleted);
    const currentIndex = activeItems.findIndex(item => item.id === id);
    
    if (currentIndex > 0) {
      const newItems = [...timelineItems];
      const itemToMove = newItems.find(item => item.id === id);
      const itemToSwap = newItems.find(item => item.id === activeItems[currentIndex - 1].id);
      
      if (itemToMove && itemToSwap) {
        const tempOrder = itemToMove.order_index;
        itemToMove.order_index = itemToSwap.order_index;
        itemToSwap.order_index = tempOrder;
      }
      
      setTimelineItems(newItems);
    }
  };

  const moveItemDown = (id: string) => {
    const activeItems = timelineItems.filter(item => !item.isDeleted);
    const currentIndex = activeItems.findIndex(item => item.id === id);
    
    if (currentIndex < activeItems.length - 1) {
      const newItems = [...timelineItems];
      const itemToMove = newItems.find(item => item.id === id);
      const itemToSwap = newItems.find(item => item.id === activeItems[currentIndex + 1].id);
      
      if (itemToMove && itemToSwap) {
        const tempOrder = itemToMove.order_index;
        itemToMove.order_index = itemToSwap.order_index;
        itemToSwap.order_index = tempOrder;
      }
      
      setTimelineItems(newItems);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare timeline items for API
      const timelineToSave = timelineItems
        .filter(item => !item.isDeleted && item.title.trim() && item.date.trim())
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        .map((item, index) => ({
          date: item.date,
          title: item.title,
          description: item.description,
          emoji: item.emoji,
          order_index: index
        }));

      const updatedTimeline = await apiService.updateTimeline(clientId, timelineToSave);
      onSave(updatedTimeline);
      onClose();
    } catch (err) {
      console.error('Failed to save timeline:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const activeItems = timelineItems
    .filter(item => !item.isDeleted)
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '900px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 24px 0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0, color: '#1a1a1a' }}>
              Edit Timeline
            </h2>
            <div>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Satoshi, sans-serif',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  marginRight: '8px',
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Satoshi, sans-serif',
                  backgroundColor: '#007bff',
                  color: 'white',
                }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '20px',
              border: '1px solid #f5c6cb'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Add Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              Upcoming milestones and important dates for the project.
            </p>
            <button
              onClick={addTimelineItem}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Satoshi, sans-serif',
                backgroundColor: '#28a745',
                color: 'white',
              }}
            >
              + Add Timeline Item
            </button>
          </div>

          {/* Timeline Items List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {activeItems.map((item, index) => (
              <div
                key={item.id}
                style={{
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: item.isNew ? '#f8f9fa' : 'white',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Timeline Item {index + 1}
                  </label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {/* Move Up Button */}
                    <button
                      onClick={() => moveItemUp(item.id)}
                      disabled={index === 0}
                      style={{
                        backgroundColor: index === 0 ? '#e9ecef' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                      }}
                      title="Move up"
                    >
                      ↑
                    </button>
                    {/* Move Down Button */}
                    <button
                      onClick={() => moveItemDown(item.id)}
                      disabled={index === activeItems.length - 1}
                      style={{
                        backgroundColor: index === activeItems.length - 1 ? '#e9ecef' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: index === activeItems.length - 1 ? 'not-allowed' : 'pointer',
                      }}
                      title="Move down"
                    >
                      ↓
                    </button>
                    {/* Remove Button */}
                    <button
                      onClick={() => removeTimelineItem(item.id)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                      title="Remove item"
                    >
                      ×
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Date
                    </label>
                    <input
                      type="text"
                      value={item.date}
                      onChange={(e) => handleItemChange(item.id, 'date', e.target.value)}
                      placeholder="e.g., June 10, Mid-July"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Satoshi, sans-serif',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Title
                    </label>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => handleItemChange(item.id, 'title', e.target.value)}
                      placeholder="e.g., Rollout of AI search in LATAM"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Satoshi, sans-serif',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Emoji
                    </label>
                    <input
                      type="text"
                      value={item.emoji}
                      onChange={(e) => handleItemChange(item.id, 'emoji', e.target.value)}
                      placeholder="🏁"
                      style={{
                        width: '60px',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Satoshi, sans-serif',
                        textAlign: 'center',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                    Description
                  </label>
                  <textarea
                    value={item.description}
                    onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                    placeholder="Enter the description of this timeline item..."
                    style={{
                      width: '100%',
                      minHeight: '60px',
                      padding: '8px 12px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'Satoshi, sans-serif',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>
            ))}

            {activeItems.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                color: '#6b7280',
                fontSize: '14px'
              }}>
                No timeline items yet. Click "Add Timeline Item" to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineEditor; 